import { useState, useEffect, useCallback } from 'react';
import { apiService } from '@/shared/services/api';
import { cacheService } from '@/shared/services/cacheService';
import { CACHE_KEYS, CACHE_CONFIG } from '@/shared/config/cache';
import type {
    StudentProgressMetric,
    AvgResolutionTimeMetric,
    ExerciseAttemptsMetric,
    ActiveStudentsMetric,
    ExerciseErrorRateMetric,
    StudentAtRiskMetric,
    WeeklyActivityMetric,
    ExerciseCompletionMatrixMetric,
} from '@/admin/types';

export interface AcademicMetricsData {
    progress: StudentProgressMetric[] | null;
    resolutionTime: AvgResolutionTimeMetric | null;
    attempts: ExerciseAttemptsMetric[] | null;
    activeStudents: ActiveStudentsMetric | null;
    errorRate: ExerciseErrorRateMetric[] | null;
    atRisk: StudentAtRiskMetric[] | null;
    weeklyActivity: WeeklyActivityMetric[] | null;
    completionMatrix: ExerciseCompletionMatrixMetric[] | null;
}

export interface UseAcademicMetricsResult {
    data: AcademicMetricsData;
    loading: boolean;
    error: string | null;
    reload: () => void;
}

const EMPTY: AcademicMetricsData = {
    progress: null,
    resolutionTime: null,
    attempts: null,
    activeStudents: null,
    errorRate: null,
    atRisk: null,
    weeklyActivity: null,
    completionMatrix: null,
};

/** Claves de caché de las métricas del dashboard, particionadas por cuatrimestre (S5). */
const metricKeys = (term: string): string[] => [
    CACHE_KEYS.metricsProgress(term),
    CACHE_KEYS.metricsResolutionTime(term),
    CACHE_KEYS.metricsAttempts(term),
    CACHE_KEYS.metricsActiveStudents(term),
    CACHE_KEYS.metricsErrorRate(term),
    CACHE_KEYS.metricsAtRisk(term),
    CACHE_KEYS.metricsWeeklyActivity(term),
    CACHE_KEYS.metricsCompletionMatrix(term),
];

function tryLoadFromCache(term: string): AcademicMetricsData | null {
    const progress = cacheService.get<StudentProgressMetric[]>(CACHE_KEYS.metricsProgress(term));
    const resolutionTime = cacheService.get<AvgResolutionTimeMetric>(CACHE_KEYS.metricsResolutionTime(term));
    const attempts = cacheService.get<ExerciseAttemptsMetric[]>(CACHE_KEYS.metricsAttempts(term));
    const activeStudents = cacheService.get<ActiveStudentsMetric>(CACHE_KEYS.metricsActiveStudents(term));
    const errorRate = cacheService.get<ExerciseErrorRateMetric[]>(CACHE_KEYS.metricsErrorRate(term));
    const atRisk = cacheService.get<StudentAtRiskMetric[]>(CACHE_KEYS.metricsAtRisk(term));
    const weeklyActivity = cacheService.get<WeeklyActivityMetric[]>(CACHE_KEYS.metricsWeeklyActivity(term));
    const completionMatrix = cacheService.get<ExerciseCompletionMatrixMetric[]>(CACHE_KEYS.metricsCompletionMatrix(term));

    if (progress && resolutionTime && attempts && activeStudents && errorRate && atRisk && weeklyActivity && completionMatrix) {
        return { progress, resolutionTime, attempts, activeStudents, errorRate, atRisk, weeklyActivity, completionMatrix };
    }
    return null;
}

/**
 * Métricas del dashboard para un cuatrimestre concreto (D11/D12).
 *
 * Con `term === null` (todavía no se resolvió el cuatrimestre en curso) no dispara nada y
 * queda en `loading`. Al cambiar de cuatrimestre refetchea solo: `term` entra en las
 * dependencias de `load`.
 */
export function useAcademicMetrics(term: string | null): UseAcademicMetricsResult {
    const [data, setData] = useState<AcademicMetricsData>(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!term) {
            setLoading(true);
            return;
        }

        setLoading(true);
        setError(null);

        const cached = tryLoadFromCache(term);
        if (cached) {
            setData(cached);
            setLoading(false);
            return;
        }

        try {
            const [prog, rt, att, act, err, risk, weekly, matrix] = await Promise.all([
                apiService.getMetricsProgress(term),
                apiService.getMetricsResolutionTime(term),
                apiService.getMetricsAttempts(term),
                apiService.getMetricsActiveStudents(term),
                apiService.getMetricsErrorRate(term),
                apiService.getMetricsAtRisk(term),
                apiService.getMetricsWeeklyActivity(term),
                apiService.getMetricsCompletionMatrix(term),
            ]);

            const ttl = CACHE_CONFIG.metrics;
            const result: AcademicMetricsData = {
                progress: prog.success && prog.data ? prog.data : null,
                resolutionTime: rt.success && rt.data ? rt.data : null,
                attempts: att.success && att.data ? att.data : null,
                activeStudents: act.success && act.data ? act.data : null,
                errorRate: err.success && err.data ? err.data : null,
                atRisk: risk.success && risk.data ? risk.data : null,
                weeklyActivity: weekly.success && weekly.data ? weekly.data : null,
                completionMatrix: matrix.success && matrix.data ? matrix.data : null,
            };

            // Guardar cada métrica en caché individualmente
            if (result.progress) cacheService.set(CACHE_KEYS.metricsProgress(term), result.progress, ttl);
            if (result.resolutionTime) cacheService.set(CACHE_KEYS.metricsResolutionTime(term), result.resolutionTime, ttl);
            if (result.attempts) cacheService.set(CACHE_KEYS.metricsAttempts(term), result.attempts, ttl);
            if (result.activeStudents) cacheService.set(CACHE_KEYS.metricsActiveStudents(term), result.activeStudents, ttl);
            if (result.errorRate) cacheService.set(CACHE_KEYS.metricsErrorRate(term), result.errorRate, ttl);
            if (result.atRisk) cacheService.set(CACHE_KEYS.metricsAtRisk(term), result.atRisk, ttl);
            if (result.weeklyActivity) cacheService.set(CACHE_KEYS.metricsWeeklyActivity(term), result.weeklyActivity, ttl);
            if (result.completionMatrix) cacheService.set(CACHE_KEYS.metricsCompletionMatrix(term), result.completionMatrix, ttl);

            setData(result);
        } catch {
            setError('Error al cargar las métricas');
        } finally {
            setLoading(false);
        }
    }, [term]);

    const reload = useCallback(() => {
        // Invalidar las claves del cuatrimestre actual antes de refetchear
        if (term) metricKeys(term).forEach(key => cacheService.invalidate(key));
        load();
    }, [load, term]);

    useEffect(() => {
        load();
    }, [load]);

    return { data, loading, error, reload };
}

